import { ArticleItem } from './articles';
import { EventItem } from './events';

export const WEBSITE_URL = 'https://marigrp.com';
export const MARI_ORGANIZATION_ID = `${WEBSITE_URL}/#organization`;
export const MARI_WEBSITE_ID = `${WEBSITE_URL}/#website`;

export const PRIVACY_CHOICES_URL =
  'https://privacyportal-uk.onetrust.com/webform/f2e9c39a-4725-4b77-b422-d0c55ef6c80c/d88c650d-5c3c-4053-8121-b8df78eb0c07?website=marigrp.com';

export interface BreadcrumbItem {
  name: string;
  path: string;
}

const PORTFOLIO_BRANDS = [
  { name: 'Frieze', url: 'https://www.frieze.com/' },
  { name: 'Miami Open presented by Itau', url: 'https://www.miamiopen.com/' },
  { name: 'Mutua Madrid Open', url: 'https://mutuamadridopen.com/' },
  { name: 'Nike Melbourne Marathon', url: 'https://melbournemarathon.com.au/' },
  { name: 'Barrett-Jackson', url: 'https://www.barrett-jackson.com/' },
  {
    name: 'Hyde Park Winter Wonderland',
    url: 'https://hydeparkwinterwonderland.com/',
  },
  { name: 'TodayTix Group', url: 'https://www.todaytix.com/' },
  { name: 'Bucket Listers', url: 'https://bucketlisters.com/' },
];

export function createHomepageSchemas(seoDescription: string): object[] {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': MARI_ORGANIZATION_ID,
      name: 'MARI Group',
      legalName: 'MARI Group',
      url: WEBSITE_URL,
      logo: `${WEBSITE_URL}/logo.svg`,
      description: seoDescription,
      founder: {
        '@type': 'Person',
        name: 'Ariel Emanuel',
      },
      location: 'Beverly Hills, California',
      sameAs: [
        'https://www.linkedin.com/company/mari-grp',
        'https://www.instagram.com/themarigroup',
        'https://x.com/themarigroup',
      ],
      subOrganization: PORTFOLIO_BRANDS.map((brand) => ({
        '@type': 'Organization',
        name: brand.name,
        url: brand.url,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': MARI_WEBSITE_ID,
      url: WEBSITE_URL,
      name: 'MARI Group',
      publisher: {
        '@id': MARI_ORGANIZATION_ID,
      },
    },
  ];
}

export function createAboutPageSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${WEBSITE_URL}/about#about-page`,
    url: `${WEBSITE_URL}/about`,
    name: 'About MARI Group',
    mainEntity: {
      '@id': MARI_ORGANIZATION_ID,
    },
  };
}

export function createPortfolioOrganizationSchemas(events: EventItem[]): object[] {
  return events.map((event) => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${WEBSITE_URL}/portfolio#${event.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')}`,
    name: event.title.trim(),
    url: event.website,
    parentOrganization: {
      '@id': MARI_ORGANIZATION_ID,
    },
  }));
}

export function createPressCollectionSchema(articles: ArticleItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${WEBSITE_URL}/press#collection-page`,
    url: `${WEBSITE_URL}/press`,
    name: 'MARI Press',
    mainEntity: {
      '@id': MARI_ORGANIZATION_ID,
    },
    hasPart: articles.map((article) => ({
      '@type': 'NewsArticle',
      '@id': `${WEBSITE_URL}/press/${article.slug}#news-article`,
      headline: article.title,
      url: `${WEBSITE_URL}/press/${article.slug}`,
      datePublished: toIsoDate(article.date),
      dateModified: toIsoDate(article.date),
    })),
  };
}

export function createPressArticleSchema(article: ArticleItem): object {
  const articleUrl = `${WEBSITE_URL}/press/${article.slug}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    '@id': `${articleUrl}#news-article`,
    headline: article.title,
    description: article.description,
    datePublished: toIsoDate(article.date),
    dateModified: toIsoDate(article.date),
    author: {
      '@type': 'Organization',
      name: 'MARI Group Editorial Team',
    },
    publisher: {
      '@id': MARI_ORGANIZATION_ID,
    },
    image: [`${WEBSITE_URL}/og-img.jpg`],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };
}

export function createBreadcrumbList(items: BreadcrumbItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${WEBSITE_URL}${item.path}`,
    })),
  };
}

function toIsoDate(value: string): string {
  const normalized = value.trim().replace(/\s+/g, ' ').toUpperCase();
  const match = normalized.match(/^([A-Z]{3})\s+(\d{1,2}),\s+(\d{4})$/);

  if (!match) {
    return '2026-01-01';
  }

  const monthMap: Record<string, string> = {
    JAN: '01',
    FEB: '02',
    MAR: '03',
    APR: '04',
    MAY: '05',
    JUN: '06',
    JUL: '07',
    AUG: '08',
    SEP: '09',
    OCT: '10',
    NOV: '11',
    DEC: '12',
  };

  const month = monthMap[match[1]];
  const day = match[2].padStart(2, '0');
  const year = match[3];

  if (!month) {
    return '2026-01-01';
  }

  return `${year}-${month}-${day}`;
}
